/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch } from "react-redux"
import { AppDispatch } from "../.."
import * as wjCore from "@grapecity/wijmo"
import { sleep, useRFC380Reader } from "./useRFC380Reader"
import { useCallback, useEffect, useState } from "react"
import { clearAlert, setAlerts } from "../../globalStore/alertStore"
import _ from "lodash"

/**
 * RFC380を利用して社員証読取を行うためのカスタムフックです
 * @param FunctionCardRead カード読み取り後に動作させる関数です。
 *
 * 以下 返却オブジェクト内properties
 *
 * scanCardReader:カードリーダを認識させる関数です
 *
 * NfcCardReader:認識したカードリーダそのもののクラスです
 *
 * deviceStatus:カード読み取り中であるかを返却するbool値です。true=読取中
 *
 * NFCReadOFF: カードの読み取りループを止める関数
 */
const useSyainCardReader: (
  FunctionCardRead: (cardId: string) => Promise<unknown>
) => {
  scanCardReader: () => Promise<void>
  NfcCardReader: USBDevice | undefined
  deviceStatus: boolean
  NFCReadOFF: Function
} = (FunctionCardRead) => {
  const [
    NfcCardReader,
    initReader,
    openReaderSession,
    closeReaderSession,
    pollingFelica,
  ] = useRFC380Reader()

  const dispatch: AppDispatch = useDispatch()
  const [deviceStatus, setDeviceStatus] = useState(false)

  const scanCardReader = useCallback(async () => {
    dispatch(clearAlert())

    if (NfcCardReader) {
      if (deviceStatus) return
      setDeviceStatus(true)
    } else {
      await initReader()
    }
  }, [NfcCardReader, deviceStatus, dispatch, initReader])

  useEffect(() => {
    initReader()
      .then(() => {
        //認識できていた場合
        setDeviceStatus(true)
      })
      .catch(() => {})
  }, [initReader])

  const cardRead = useCallback(async () => {
    localStorage.setItem("NFC-Loop", "ON")
    console.log("loop 開始")
    await openReaderSession()
    await sleep(50)
    let strCardId

    // 30回読取をしても読めない場合止める
    let loopCount = 0
    const maxLoopCount = 30
    do {
      const cardIdArray = await pollingFelica()

      await sleep(100)

      strCardId = cardIdArray
        ?.map((byte) => _.padStart(byte.toString(16).toUpperCase(), 2, "0"))
        .join("-")
      loopCount++
    } while (
      wjCore.isNullOrWhiteSpace(strCardId ?? "") &&
      localStorage.getItem("NFC-Loop") === "ON" &&
      loopCount < maxLoopCount
    )

    await closeReaderSession()

    if (localStorage.getItem("NFC-Loop") === "OFF") return

    if (strCardId == null || loopCount >= maxLoopCount) {
      alert("カードが読み取れませんでした")
      setDeviceStatus(false)
      return
    } else {
      console.log(`%c${strCardId}`, "font-size:2rem; color:green;")
    }
    try {
      await FunctionCardRead(strCardId)

      localStorage.setItem("NFC-Loop", "OFF")
    } catch (error) {
      dispatch(setAlerts(error))
      // 読み取れない場合は再度ポーリング開始
      await cardRead()
      // alert("カードが読み取れませんでした")
      // setDeviceStatus(false)
    }

    setDeviceStatus(false)
  }, [
    FunctionCardRead,
    closeReaderSession,
    dispatch,
    openReaderSession,
    pollingFelica,
  ])

  //polling useEffect
  useEffect(() => {
    if (
      FunctionCardRead == null ||
      NfcCardReader == null ||
      closeReaderSession == null ||
      deviceStatus == null ||
      dispatch == null ||
      openReaderSession == null ||
      pollingFelica == null
    )
      return

    switch (deviceStatus) {
      case false:
        break
      case true:
        if (NfcCardReader == null || NfcCardReader.opened) return

        cardRead().catch((error) => {
          if (NfcCardReader && NfcCardReader.opened) {
            NfcCardReader.close()
          }
          setDeviceStatus(false)
        })
        break
      default:
        break
    }
  }, [deviceStatus])

  useEffect(() => {
    return () => {
      localStorage.setItem("NFC-Loop", "OFF")
    }
  }, [])

  return {
    scanCardReader,
    deviceStatus,
    NfcCardReader,
    NFCReadOFF: () => {
      localStorage.setItem("NFC-Loop", "OFF")
    },
  }
}

export default useSyainCardReader
